import { useEffect, useMemo } from "react";
import { createBrowserRouter, RouterProvider, type RouteObject } from "react-router";
import { AppFrame } from "../layout/shell";
import { SettingsPage } from "../pages/SettingsPage";
import { NotFound } from "../pages/NotFound";
import { HomePage } from "../pages/HomePage";
import { LoginPage, RegisterPage, RequireAuth } from "../pages/AuthPages";
import { ProjectsPage } from "../pages/ProjectsPage";
import { CredentialsPage } from "../pages/CredentialsPage";
import { NewAgentPage } from "../pages/NewAgentPage";
import { ProjectPage } from "../pages/ProjectPage";
import { TemplateEditorPage, TemplatesPage } from "../team/Templates";
import { TasksPage } from "../tasks/TasksPage";
import { TaskDetailPage } from "../tasks/TaskDetail";
import { PullsPage } from "../pulls/PullsPage";
import { PullDetailPage } from "../pulls/PullDetail";
import { MailPage } from "../mail/MailPage";
import { RoomsPage } from "../rooms/RoomsPage";
import { AssetsPage } from "../assets/AssetsPage";
import { AssetDetailPage } from "../assets/AssetDetail";
import { useServices } from "./services";
import { MeetingRoomPage, MeetingsPage } from "../meetings/MeetingsPage";
import { BillingPage } from "../billing/BillingPage";
import { ListingPage, MarketPage } from "../market/MarketPages";
import { CapabilityDetailPage, CapabilityLibraryPage } from "../capabilities/LibraryPages";
import { CapabilityEditorPage } from "../capabilities/EditorPage";
import { AgentCapabilitiesPage } from "../capabilities/AgentCapsPage";
import { McpServersPage, MediaProvidersPage, SecretsPage } from "../capabilities/IntegrationPages";
import { AdminIndex, AuditPage, MarketReviewPage, NodesPage, PlacementsPage } from "../admin/AdminPages";

export const appRoutes: RouteObject[] = [
  { path: "login", element: <LoginPage /> },
  { path: "register", element: <RegisterPage /> },
  {
    element: (
      <RequireAuth>
        <AppFrame />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: "t/:tid", element: <ProjectsPage /> },
      { path: "t/:tid/credentials", element: <CredentialsPage /> },
      { path: "t/:tid/templates", element: <TemplatesPage /> },
      { path: "t/:tid/templates/new", element: <TemplateEditorPage /> },
      { path: "t/:tid/templates/:tplid", element: <TemplateEditorPage /> },
      { path: "p/:pid", element: <ProjectPage /> },
      { path: "p/:pid/new", element: <NewAgentPage /> },
      { path: "p/:pid/a/:aid", element: <ProjectPage /> },
      { path: "p/:pid/a/:aid/capabilities", element: <AgentCapabilitiesPage /> },
      { path: "p/:pid/tasks", element: <TasksPage /> },
      { path: "p/:pid/tasks/:tkid", element: <TaskDetailPage /> },
      { path: "p/:pid/pulls", element: <PullsPage /> },
      { path: "p/:pid/pulls/:rid/:num", element: <PullDetailPage /> },
      { path: "p/:pid/mail", element: <MailPage /> },
      { path: "p/:pid/rooms", element: <RoomsPage /> },
      { path: "p/:pid/rooms/:cid", element: <RoomsPage /> },
      { path: "p/:pid/assets", element: <AssetsPage /> },
      { path: "p/:pid/assets/:asid", element: <AssetDetailPage /> },
      { path: "p/:pid/meetings", element: <MeetingsPage /> },
      { path: "p/:pid/meetings/:mid", element: <MeetingRoomPage /> },
      { path: "t/:tid/billing", element: <BillingPage section="usage" /> },
      { path: "t/:tid/billing/wallet", element: <BillingPage section="wallet" /> },
      { path: "t/:tid/billing/invoices", element: <BillingPage section="invoices" /> },
      { path: "t/:tid/billing/budget", element: <BillingPage section="budget" /> },
      { path: "t/:tid/market", element: <MarketPage section="browse" /> },
      { path: "t/:tid/market/mine", element: <MarketPage section="mine" /> },
      { path: "t/:tid/market/rentals", element: <MarketPage section="rentals" /> },
      { path: "t/:tid/market/l/:lid", element: <ListingPage /> },
      { path: "t/:tid/capabilities", element: <CapabilityLibraryPage /> },
      { path: "t/:tid/capabilities/new", element: <CapabilityEditorPage /> },
      { path: "t/:tid/capabilities/:capid", element: <CapabilityDetailPage /> },
      { path: "t/:tid/capabilities/:capid/edit", element: <CapabilityEditorPage /> },
      { path: "t/:tid/integrations", element: <SecretsPage /> },
      { path: "t/:tid/integrations/secrets", element: <SecretsPage /> },
      { path: "t/:tid/integrations/mcp", element: <McpServersPage /> },
      { path: "t/:tid/integrations/media", element: <MediaProvidersPage /> },
      { path: "admin", element: <AdminIndex /> },
      { path: "admin/nodes", element: <NodesPage /> },
      { path: "admin/placements", element: <PlacementsPage /> },
      { path: "admin/audit", element: <AuditPage /> },
      { path: "admin/market", element: <MarketReviewPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export function App() {
  const router = useMemo(() => createBrowserRouter(appRoutes), []);
  const { native } = useServices();
  // 原生壳：深链接 / 点击通知用路由跳转（不整页刷新）
  useEffect(() => {
    if (!native) return;
    native.setNavigator((p) => void router.navigate(p));
    return () => native.setNavigator(null);
  }, [native, router]);
  return <RouterProvider router={router} />;
}
